import { useMemo, useState } from 'react';

import { useExperimentCategoryPerformance } from '../../hooks/use-experiment-category-performance';
import { useExperimentDataDownloads } from '../../hooks/use-experiment-data-downloads';

import { ExperimentCategoryDailyDidLiftTable } from './experiment-category-daily-did-lift-table';
import { ExperimentCategoryKpiCards } from './experiment-category-kpi-cards';
import { ExperimentCategoryPerformanceTable } from './experiment-category-performance-table';
import { ExperimentDetailedDataDownloads } from './experiment-detailed-data-downloads';
import { ExperimentWeeklyDidRevenueSection } from './experiment-weekly-did-revenue-section';

interface Props {
  experimentId: string;
  /** Encoded `?segment=` product filter (see `encodeSegmentFilter`); `undefined` = unscoped. */
  segment?: string | undefined;
}

const DEFAULT_CATEGORY = 'womens-pajama-set';

/**
 * Performance-tab content rendered for tenants on the category-experiment view
 * (screenshot 17). Owns the selected-category state that drives both the
 * Weekly DiD Revenue Trend section and any future per-category sub-panels.
 *
 * Phase 4 ships the KPI row + Weekly DiD Revenue Section. Deferred:
 *   - Category Performance — Revenue Weighted table (per-category DID metrics)
 *   - Daily DID Lift Analysis (categorical)
 *   - Detailed Data Downloads (L2 / L3 CSV cards)
 */
export function ExperimentCategoryPerformancePanel({ experimentId, segment }: Props) {
  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_CATEGORY);
  const { data, isLoading, isError } = useExperimentCategoryPerformance(
    experimentId,
    selectedCategory,
    { segment },
  );
  const {
    data: dataDownloads,
    isLoading: isDataDownloadsLoading,
    isError: isDataDownloadsError,
  } = useExperimentDataDownloads(experimentId, segment);

  // Snap to the first available category if the user's current pick isn't on
  // the list returned by the backend (e.g. the default doesn't exist for this
  // tenant). Derived during render so we never `setState` inside an effect.
  const effectiveCategory = useMemo(() => {
    const options = data?.availableCategories;
    if (!options || options.length === 0) return selectedCategory;
    if (options.some((o) => o.value === selectedCategory)) return selectedCategory;
    return options[0]?.value ?? selectedCategory;
  }, [data, selectedCategory]);

  return (
    <div className="space-y-6">
      <ExperimentCategoryKpiCards kpis={data?.kpis} isLoading={isLoading} />

      <ExperimentCategoryPerformanceTable
        rows={data?.categoryPerformanceRows ?? []}
        isLoading={isLoading}
        revenueSource={data?.revenueSource}
        isError={isError}
      />

      <ExperimentWeeklyDidRevenueSection
        availableCategories={data?.availableCategories ?? []}
        selectedCategory={effectiveCategory}
        onCategoryChange={setSelectedCategory}
        weeklyPoints={data?.weeklyDidRevenue ?? []}
        revenuePoints={data?.testVsControlRevenue ?? []}
        isLoading={isLoading}
        revenueSource={data?.revenueSource}
      />

      <ExperimentCategoryDailyDidLiftTable
        rows={data?.dailyDidLift ?? []}
        isLoading={isLoading}
        revenueSource={data?.revenueSource}
        isError={isError}
      />

      <ExperimentDetailedDataDownloads
        experimentId={experimentId}
        cards={dataDownloads ?? []}
        isLoading={isDataDownloadsLoading}
        isError={isDataDownloadsError}
        segment={segment}
      />
    </div>
  );
}
